import type { ChapterNumberingOverride } from "../lib/chapterNumbering";
import type { MapPage } from "../lib/generated/zwMap";
/**
 * Pure TEA update for the project workspace store (#237).
 *
 * Model / Msg / update / Cmd only. No Pinia, Vue, invoke, Tauri, or other
 * stores. ESLint enforces that on `*.update.ts`. Interpret Cmds in
 * `useProjectWorkspaceStore.ts` via `dispatch` from `lib/tea`.
 *
 * Manuscript reorder msgs rewrite Model and emit `writeManuscript`. Park /
 * notes / database CRUD msgs are Cmd-only (backend returns a fresh payload);
 * the shell still owns confirm / save flush before dispatch, and invoke +
 * `applyPayload` in the Cmd runner.
 *
 * Cross-chapter scene move invoke still lives in the shell; collision dialog
 * open/clear is Model state here so retry UI is Msg-driven.
 */
import { assertNever } from "../lib/assertNever";
import { isNothing, just, nothing, toMaybe, type Maybe } from "../lib/maybe";
import { reorderScenesExactSlot } from "../lib/reorderExactSlot";
import {
  flattenChapters,
  insertMapFigure,
  moveActBefore,
  moveChapterToSlot,
  moveMapBefore,
  removeMapFigure,
  replaceChapter,
  replaceMapFigure,
  type ManuscriptItem,
  type ManuscriptSlot,
  type ZwMapFigure,
} from "../lib/zwConfig";

export type WorkspaceSceneRef = {
  readonly folder: string;
  readonly sceneFile: string;
};

export type WorkspaceSceneDropTarget = {
  readonly folder: string;
  readonly sceneFile: Maybe<string>;
};

export type SceneMoveCollisionState = {
  readonly from: WorkspaceSceneRef;
  readonly to: WorkspaceSceneDropTarget;
  readonly attemptedName: string;
  readonly chapterName: string;
  readonly error: Maybe<string>;
};

/** Manuscript + collision-dialog slice. */
export type WorkspaceModel = {
  readonly manuscript: ManuscriptItem[];
  readonly sceneMoveCollision: Maybe<SceneMoveCollisionState>;
};

export type WorkspaceMsg =
  | {
      readonly type: "MoveChapterToSlot";
      readonly chapterFolder: string;
      readonly slot: ManuscriptSlot;
    }
  | {
      readonly type: "MoveAct";
      readonly actFolder: string;
      readonly beforeFolder: Maybe<string>;
    }
  | {
      readonly type: "IncludeMap";
      readonly figure: ZwMapFigure;
      readonly atIndex: number;
    }
  | {
      readonly type: "RemoveMap";
      readonly id: string;
    }
  | {
      readonly type: "MoveMap";
      readonly id: string;
      readonly beforeKey: Maybe<string>;
    }
  | {
      readonly type: "UpdateMapFigure";
      readonly id: string;
      readonly figure: ZwMapFigure;
    }
  | {
      readonly type: "ReorderScenesSameChapter";
      readonly folder: string;
      readonly fromFile: string;
      readonly toFile: Maybe<string>;
    }
  | {
      readonly type: "OpenSceneMoveCollision";
      readonly collision: SceneMoveCollisionState;
    }
  | { readonly type: "ClearSceneMoveCollision" }
  // Park / unpark (shell: save flush + active-dirty bookkeeping in Cmd runner)
  | {
      readonly type: "ParkScene";
      readonly folder: string;
      readonly sceneFile: string;
    }
  | { readonly type: "ParkChapter"; readonly folder: string }
  | {
      readonly type: "UnparkSceneIntoChapter";
      readonly name: string;
      readonly toFolder: string;
      readonly toSceneFile: Maybe<string>;
    }
  | {
      readonly type: "UnparkChapter";
      readonly name: string;
      readonly atIndex: number;
    }
  // Notes CRUD (shell: confirm on delete, save flush on rename/delete)
  | { readonly type: "NewNote"; readonly name: string }
  | {
      readonly type: "RenameNote";
      readonly from: string;
      readonly to: string;
    }
  | { readonly type: "DeleteNote"; readonly name: string }
  // Database CRUD (shell: confirm on delete, save flush on create/rename/delete)
  | {
      readonly type: "NewDatabase";
      readonly name: string;
      readonly entryKind: "entry" | "map";
      readonly mapPage: Maybe<MapPage>;
    }
  | {
      readonly type: "RenameDatabase";
      readonly from: string;
      readonly to: string;
    }
  | { readonly type: "DeleteDatabase"; readonly name: string }
  // Act / chapter / scene CRUD + synopsis (shell: confirm / save flush / after resolution)
  | {
      readonly type: "CreateAct";
      readonly name: string;
      readonly afterFolder: Maybe<string>;
    }
  | {
      readonly type: "RenameAct";
      readonly fromFolder: string;
      readonly toFolder: string;
    }
  | { readonly type: "DeleteAct"; readonly folder: string }
  | { readonly type: "NewChapter"; readonly name: string }
  | {
      readonly type: "NewChapterAt";
      readonly name: string;
      readonly actFolder: Maybe<string>;
    }
  | {
      readonly type: "RenameChapter";
      readonly fromFolder: string;
      readonly toFolder: string;
    }
  | { readonly type: "DeleteChapter"; readonly folder: string }
  | { readonly type: "NewScene"; readonly folder: string }
  | {
      readonly type: "RenameScene";
      readonly folder: string;
      readonly fromSceneFile: string;
      readonly to: string;
    }
  | {
      readonly type: "DeleteScene";
      readonly folder: string;
      readonly sceneFile: string;
    }
  | {
      readonly type: "UpdateChapterSynopsis";
      readonly folder: string;
      readonly synopsis: string;
    }
  | {
      readonly type: "UpdateChapterNumbering";
      readonly folder: string;
      readonly numbering: ChapterNumberingOverride;
    }
  | {
      readonly type: "UpdateSceneSynopsis";
      readonly folder: string;
      readonly sceneFile: string;
      readonly synopsis: string;
    }
  // Cross-chapter scene move (shell checks "at least one scene" before dispatch)
  | {
      readonly type: "MoveSceneAcrossChapters";
      readonly from: WorkspaceSceneRef;
      readonly to: WorkspaceSceneDropTarget;
      readonly toSceneFile: Maybe<string>;
      readonly retrying: boolean;
    }
  // Trash + parked-item rename/delete
  | {
      readonly type: "RestoreDeleted";
      readonly relativePath: string;
      readonly oid: string;
    }
  | {
      readonly type: "PreviewDeleted";
      readonly relativePath: string;
      readonly oid: string;
    }
  | {
      readonly type: "RenameParkedItem";
      readonly from: string;
      readonly to: string;
    }
  | { readonly type: "DeleteParkedItem"; readonly name: string }
  // Select / open (shell: last-file pointers; Cmd: navigate + switch/open)
  | {
      readonly type: "SelectManuscriptFile" | "SelectManuscriptMap";
      readonly name: string;
      readonly skipNavigate: boolean;
    }
  | {
      readonly type: "SelectNote";
      readonly name: string;
      readonly skipNavigate: boolean;
    }
  | {
      readonly type: "SelectParkedItem";
      readonly name: string;
      readonly skipNavigate: boolean;
    }
  | {
      readonly type: "SelectDatabase";
      readonly name: string;
      readonly skipNavigate: boolean;
    }
  | {
      readonly type: "JumpInFile";
      readonly name: string;
      readonly from: number;
    }
  | {
      readonly type: "SearchJump";
      readonly source: "chapter" | "note" | "database";
      readonly name: string;
      readonly from: number;
    };

export type WorkspaceCmd =
  | {
      readonly kind: "writeManuscript";
      readonly manuscript: ManuscriptItem[];
    }
  | {
      readonly kind: "parkScene";
      readonly folder: string;
      readonly sceneFile: string;
    }
  | { readonly kind: "parkChapter"; readonly folder: string }
  | {
      readonly kind: "unparkScene";
      readonly name: string;
      readonly toFolder: string;
      readonly toSceneFile: Maybe<string>;
    }
  | {
      readonly kind: "unparkChapter";
      readonly name: string;
      readonly atIndex: number;
    }
  | { readonly kind: "createNote"; readonly name: string }
  | {
      readonly kind: "renameNote";
      readonly from: string;
      readonly to: string;
    }
  | { readonly kind: "deleteNote"; readonly name: string }
  | {
      readonly kind: "createDatabase";
      readonly name: string;
      readonly entryKind: "entry" | "map";
      readonly mapPage: Maybe<MapPage>;
    }
  | {
      readonly kind: "renameDatabase";
      readonly from: string;
      readonly to: string;
    }
  | { readonly kind: "deleteDatabase"; readonly name: string }
  | {
      readonly kind: "createAct";
      readonly name: string;
      readonly afterFolder: Maybe<string>;
    }
  | {
      readonly kind: "renameAct";
      readonly fromFolder: string;
      readonly toFolder: string;
    }
  | { readonly kind: "deleteAct"; readonly folder: string }
  | { readonly kind: "createChapter"; readonly name: string }
  | {
      readonly kind: "createChapterAt";
      readonly name: string;
      readonly actFolder: Maybe<string>;
    }
  | {
      readonly kind: "renameChapter";
      readonly fromFolder: string;
      readonly toFolder: string;
    }
  | { readonly kind: "deleteChapter"; readonly folder: string }
  | { readonly kind: "createScene"; readonly folder: string }
  | {
      readonly kind: "renameScene";
      readonly folder: string;
      readonly fromSceneFile: string;
      readonly to: string;
    }
  | {
      readonly kind: "deleteScene";
      readonly folder: string;
      readonly sceneFile: string;
    }
  | {
      readonly kind: "updateChapterSynopsis";
      readonly folder: string;
      readonly synopsis: string;
    }
  | {
      readonly kind: "updateChapterNumbering";
      readonly folder: string;
      readonly numbering: ChapterNumberingOverride;
    }
  | {
      readonly kind: "updateSceneSynopsis";
      readonly folder: string;
      readonly sceneFile: string;
      readonly synopsis: string;
    }
  | {
      readonly kind: "moveSceneAcrossChapters";
      readonly from: WorkspaceSceneRef;
      readonly to: WorkspaceSceneDropTarget;
      readonly toSceneFile: Maybe<string>;
      readonly retrying: boolean;
    }
  | {
      readonly kind: "restoreDeleted";
      readonly relativePath: string;
      readonly oid: string;
    }
  | {
      readonly kind: "previewDeleted";
      readonly relativePath: string;
      readonly oid: string;
    }
  | {
      readonly kind: "renameParkedItem";
      readonly from: string;
      readonly to: string;
    }
  | { readonly kind: "deleteParkedItem"; readonly name: string }
  | {
      readonly kind: "selectManuscriptFile" | "selectManuscriptMap";
      readonly name: string;
      readonly skipNavigate: boolean;
    }
  | {
      readonly kind: "selectNote";
      readonly name: string;
      readonly skipNavigate: boolean;
    }
  | {
      readonly kind: "selectParkedItem";
      readonly name: string;
      readonly skipNavigate: boolean;
    }
  | {
      readonly kind: "selectDatabase";
      readonly name: string;
      readonly skipNavigate: boolean;
    }
  | {
      readonly kind: "jumpInFile";
      readonly name: string;
      readonly from: number;
    }
  | {
      readonly kind: "searchJump";
      readonly source: "chapter" | "note" | "database";
      readonly name: string;
      readonly from: number;
    };

function noChange(
  model: WorkspaceModel,
): readonly [WorkspaceModel, readonly WorkspaceCmd[]] {
  return [model, []];
}

function withWrittenManuscript(
  model: WorkspaceModel,
  manuscript: ManuscriptItem[],
): readonly [WorkspaceModel, readonly WorkspaceCmd[]] {
  if (manuscript === model.manuscript) return noChange(model);
  return [{ ...model, manuscript }, [{ kind: "writeManuscript", manuscript }]];
}

/** Pure `(model, msg) → [model, Cmd[]]`. */
export function updateWorkspace(
  model: WorkspaceModel,
  msg: WorkspaceMsg,
): readonly [WorkspaceModel, readonly WorkspaceCmd[]] {
  switch (msg.type) {
    case "MoveChapterToSlot": {
      const next = moveChapterToSlot(
        model.manuscript,
        msg.chapterFolder,
        msg.slot,
      );
      return withWrittenManuscript(model, next);
    }
    case "MoveAct": {
      const next = moveActBefore(
        model.manuscript,
        msg.actFolder,
        msg.beforeFolder,
      );
      return withWrittenManuscript(model, next);
    }
    case "IncludeMap": {
      const already = model.manuscript.some(
        (item) =>
          item.type === "map" &&
          (item.id === msg.figure.id || item.mapPath === msg.figure.mapPath),
      );
      if (already) return noChange(model);
      const next = insertMapFigure(model.manuscript, msg.figure, msg.atIndex);
      return withWrittenManuscript(model, next);
    }
    case "RemoveMap": {
      const next = removeMapFigure(model.manuscript, msg.id);
      return withWrittenManuscript(model, next);
    }
    case "MoveMap": {
      const next = moveMapBefore(model.manuscript, msg.id, msg.beforeKey);
      return withWrittenManuscript(model, next);
    }
    case "UpdateMapFigure": {
      const next = replaceMapFigure(model.manuscript, msg.id, msg.figure);
      return withWrittenManuscript(model, next);
    }
    case "ReorderScenesSameChapter": {
      const chapter = toMaybe(
        flattenChapters(model.manuscript).find((c) => c.folder === msg.folder),
      );
      if (isNothing(chapter)) return noChange(model);
      const scenes = reorderScenesExactSlot(
        chapter.value.scenes,
        msg.fromFile,
        msg.toFile,
      );
      // reorderExactSlot always copies; compare keys so a self-drop is a no-op.
      const unchanged =
        scenes.length === chapter.value.scenes.length &&
        scenes.every(
          (scene, i) => scene.file === chapter.value.scenes[i]?.file,
        );
      if (unchanged) return noChange(model);
      const next = replaceChapter(model.manuscript, msg.folder, {
        ...chapter.value,
        scenes,
      });
      return withWrittenManuscript(model, next);
    }
    case "OpenSceneMoveCollision":
      return [{ ...model, sceneMoveCollision: just(msg.collision) }, []];
    case "ClearSceneMoveCollision":
      if (isNothing(model.sceneMoveCollision)) return noChange(model);
      return [{ ...model, sceneMoveCollision: nothing }, []];
    case "ParkScene":
      return [
        model,
        [
          {
            kind: "parkScene",
            folder: msg.folder,
            sceneFile: msg.sceneFile,
          },
        ],
      ];
    case "ParkChapter":
      return [model, [{ kind: "parkChapter", folder: msg.folder }]];
    case "UnparkSceneIntoChapter":
      return [
        model,
        [
          {
            kind: "unparkScene",
            name: msg.name,
            toFolder: msg.toFolder,
            toSceneFile: msg.toSceneFile,
          },
        ],
      ];
    case "UnparkChapter":
      return [
        model,
        [
          {
            kind: "unparkChapter",
            name: msg.name,
            atIndex: msg.atIndex,
          },
        ],
      ];
    case "NewNote":
      return [model, [{ kind: "createNote", name: msg.name }]];
    case "RenameNote":
      return [model, [{ kind: "renameNote", from: msg.from, to: msg.to }]];
    case "DeleteNote":
      return [model, [{ kind: "deleteNote", name: msg.name }]];
    case "NewDatabase":
      return [
        model,
        [
          {
            kind: "createDatabase",
            name: msg.name,
            entryKind: msg.entryKind,
            mapPage: msg.mapPage,
          },
        ],
      ];
    case "RenameDatabase":
      return [model, [{ kind: "renameDatabase", from: msg.from, to: msg.to }]];
    case "DeleteDatabase":
      return [model, [{ kind: "deleteDatabase", name: msg.name }]];
    case "CreateAct":
      return [
        model,
        [
          {
            kind: "createAct",
            name: msg.name,
            afterFolder: msg.afterFolder,
          },
        ],
      ];
    case "RenameAct":
      return [
        model,
        [
          {
            kind: "renameAct",
            fromFolder: msg.fromFolder,
            toFolder: msg.toFolder,
          },
        ],
      ];
    case "DeleteAct":
      return [model, [{ kind: "deleteAct", folder: msg.folder }]];
    case "NewChapterAt":
      return [
        model,
        [{ kind: "createChapterAt", name: msg.name, actFolder: msg.actFolder }],
      ];
    case "NewChapter":
      return [model, [{ kind: "createChapter", name: msg.name }]];
    case "RenameChapter":
      return [
        model,
        [
          {
            kind: "renameChapter",
            fromFolder: msg.fromFolder,
            toFolder: msg.toFolder,
          },
        ],
      ];
    case "DeleteChapter":
      return [model, [{ kind: "deleteChapter", folder: msg.folder }]];
    case "NewScene":
      return [model, [{ kind: "createScene", folder: msg.folder }]];
    case "RenameScene":
      return [
        model,
        [
          {
            kind: "renameScene",
            folder: msg.folder,
            fromSceneFile: msg.fromSceneFile,
            to: msg.to,
          },
        ],
      ];
    case "DeleteScene":
      return [
        model,
        [
          {
            kind: "deleteScene",
            folder: msg.folder,
            sceneFile: msg.sceneFile,
          },
        ],
      ];
    case "UpdateChapterSynopsis":
      return [
        model,
        [
          {
            kind: "updateChapterSynopsis",
            folder: msg.folder,
            synopsis: msg.synopsis,
          },
        ],
      ];
    case "UpdateChapterNumbering":
      return [
        model,
        [
          {
            kind: "updateChapterNumbering",
            folder: msg.folder,
            numbering: msg.numbering,
          },
        ],
      ];
    case "UpdateSceneSynopsis":
      return [
        model,
        [
          {
            kind: "updateSceneSynopsis",
            folder: msg.folder,
            sceneFile: msg.sceneFile,
            synopsis: msg.synopsis,
          },
        ],
      ];
    case "MoveSceneAcrossChapters":
      return [
        model,
        [
          {
            kind: "moveSceneAcrossChapters",
            from: msg.from,
            to: msg.to,
            toSceneFile: msg.toSceneFile,
            retrying: msg.retrying,
          },
        ],
      ];
    case "RestoreDeleted":
      return [
        model,
        [
          {
            kind: "restoreDeleted",
            relativePath: msg.relativePath,
            oid: msg.oid,
          },
        ],
      ];
    case "PreviewDeleted":
      return [
        model,
        [
          {
            kind: "previewDeleted",
            relativePath: msg.relativePath,
            oid: msg.oid,
          },
        ],
      ];
    case "RenameParkedItem":
      return [
        model,
        [{ kind: "renameParkedItem", from: msg.from, to: msg.to }],
      ];
    case "DeleteParkedItem":
      return [model, [{ kind: "deleteParkedItem", name: msg.name }]];
    case "SelectManuscriptMap":
      return [
        model,
        [
          {
            kind: "selectManuscriptMap",
            name: msg.name,
            skipNavigate: msg.skipNavigate,
          },
        ],
      ];
    case "SelectManuscriptFile":
      return [
        model,
        [
          {
            kind: "selectManuscriptFile",
            name: msg.name,
            skipNavigate: msg.skipNavigate,
          },
        ],
      ];
    case "SelectNote":
      return [
        model,
        [
          {
            kind: "selectNote",
            name: msg.name,
            skipNavigate: msg.skipNavigate,
          },
        ],
      ];
    case "SelectParkedItem":
      return [
        model,
        [
          {
            kind: "selectParkedItem",
            name: msg.name,
            skipNavigate: msg.skipNavigate,
          },
        ],
      ];
    case "SelectDatabase":
      return [
        model,
        [
          {
            kind: "selectDatabase",
            name: msg.name,
            skipNavigate: msg.skipNavigate,
          },
        ],
      ];
    case "JumpInFile":
      return [model, [{ kind: "jumpInFile", name: msg.name, from: msg.from }]];
    case "SearchJump":
      return [
        model,
        [
          {
            kind: "searchJump",
            source: msg.source,
            name: msg.name,
            from: msg.from,
          },
        ],
      ];
    default:
      return assertNever(msg);
  }
}
